const post = {
  "slug": "manage-instagram-orders-without-spreadsheet",
  "title": "How to Manage Instagram Orders Without a Spreadsheet",
  "seoTitle": "Manage Instagram Orders Without a Spreadsheet",
  "description": "Stop tracking Instagram orders in notes and spreadsheets. See how the Checkout DM Orders tab shows each order, payment proof, discounts, shipping and returns.",
  "excerpt": "Orders scattered across chats and sheets get lost. Here is how the Orders tab keeps every order, payment check, shipment and return in one place.",
  "category": "Product",
  "tags": [
    "Manage Instagram orders",
    "Instagram order management",
    "Order tracking for Instagram sellers",
    "Checkout DM Orders tab"
  ],
  "date": "2026-11-25",
  "cover": {
    "src": "/blog-images/manage-instagram-orders-without-spreadsheet-cover.png",
    "alt": "An Orders tab listing each Instagram order with its order number, items, total and status"
  },
  "blocks": [
    {
      "type": "p",
      "text": "Most Instagram sellers start by keeping orders in a notes app, a spreadsheet or just their DM scroll. It works for the first ten orders. By the fiftieth, you are scrolling through chats to find who paid, who has not and who is waiting for a parcel. The Checkout DM Orders tab replaces that with one list. This guide explains what each part of it shows and how to use it day to day."
    },
    {
      "type": "h2",
      "text": "One row per order"
    },
    {
      "type": "p",
      "text": "Every order placed through the bot appears in the Orders tab. Each row shows:"
    },
    {
      "type": "ul",
      "items": [
        "**Order number:** a short reference such as CDM-000140. Use it when a customer quotes it to you, or when you search for an order.",
        "**Customer:** who placed the order, linked to their Instagram profile when their handle is on file.",
        "**Items:** every product in the order, one per line, with the size if it has one and the quantity. For example, Red T-Shirt, M, times 9 means nine units in size M in a single order.",
        "**Total:** the final amount the customer paid. If a discount or shipping fee applied, a View breakup link shows the subtotal, discount and shipping separately.",
        "**Status:** where the order stands, such as pending payment, paid or returned. Tap the badge for a plain explanation. Our guide to [Checkout DM status badges](/blog/checkout-dm-status-badges-explained) lists them all.",
        "**Date:** when the order was placed."
      ]
    },
    {
      "type": "p",
      "text": "On Growth and Pro, a further column shows which discount applied: the exact coupon code the customer typed, or an Auto badge when your best automatic discount applied instead."
    },
    {
      "type": "h2",
      "text": "Confirming payments"
    },
    {
      "type": "p",
      "text": "If you take payment manually by UPI QR code, UPI ID or bank transfer, Checkout DM cannot confirm the payment for you the way it can with a gateway link. The customer sends a screenshot of the payment in the DM, and it appears in the Orders tab for you to check against your bank account. You then press Confirm or Reject. The screenshot is deleted from Checkout DM\u2019s own storage after 90 days, but it stays visible in your Instagram DM thread with that customer. If you use a gateway, payments are confirmed automatically. Our guide to [avoiding fake payment screenshots](/blog/avoid-fake-payment-screenshots-instagram-sellers) explains what to look for."
    },
    {
      "type": "h2",
      "text": "Shipping and the return window"
    },
    {
      "type": "p",
      "text": "On Starter, Growth and Pro, each order shows Not shipped yet with a Mark as Shipped button. Once you press it, the row shows the ship date. That date matters beyond your own records, because for physical orders it starts the customer\u2019s return window, plus a short buffer for transit. Until you mark an order shipped, no countdown is running. If you use Shiprocket or Shippo, our guide to [auto-shipping Instagram orders](/blog/auto-ship-instagram-orders-shiprocket-shippo) shows how to connect them. Free accounts see items and payment status, but not the shipping controls."
    },
    {
      "type": "h2",
      "text": "Returns from the same place"
    },
    {
      "type": "p",
      "text": "For a paid order with no return on file, a Log a return link lets you record one yourself, for example when an item arrived damaged or you sent the wrong size. You do not have to wait for the customer to ask. Logging a return yourself is not limited by the return window, which only applies when a customer requests one through the bot. Once a return exists, the column shows its status instead. Approving a return restocks the item automatically and notifies the customer. See [returns and exchanges in Instagram DMs](/blog/returns-and-exchanges-in-instagram-dms) for the whole flow."
    },
    {
      "type": "h2",
      "text": "Knowing your customer"
    },
    {
      "type": "p",
      "text": "The Customers tab holds each customer\u2019s name, phone, email and latest address, plus how many orders they have placed and how much they have paid in total. A history button opens a full timeline of that customer\u2019s orders and returns, which is useful before you reply to a complaint or a return. You can also correct their details there if they typed something wrong."
    },
    {
      "type": "h2",
      "text": "Exports and numbers"
    },
    {
      "type": "p",
      "text": "On Growth and Pro you can export customers, orders and returns as a CSV file, which is handy if you want to keep your own records or share them with an accountant. The Analytics page shows revenue, paid orders and conversion over 7, 30 or 90 days on Growth and Pro. Our guide to [what every number in Checkout DM Analytics means](/blog/understand-checkout-dm-analytics-numbers) explains each figure."
    },
    {
      "type": "callout",
      "tone": "lilac",
      "title": "A five-minute daily routine",
      "text": "Open Orders. Confirm any manual payments. Pack and ship what is paid, and press Mark as Shipped. Then check Returns for anything marked Requested. Because everything is in one list, you are not searching chat history for who ordered what."
    },
    {
      "type": "h2",
      "text": "Moving off a spreadsheet"
    },
    {
      "type": "ol",
      "items": [
        "Keep your old sheet for past orders, and let new orders live in the Orders tab from today.",
        "Use the order number when you talk to customers, so you can find any order quickly.",
        "Mark each order shipped as you hand it to the courier, so the return window is accurate.",
        "On Growth and Pro, export a CSV when you need a copy of your records."
      ]
    },
    {
      "type": "p",
      "text": "If you are still building your product list, start with our guide to [Instagram catalog tips](/blog/instagram-catalog-tips-photos-sizes)."
    }
  ],
  "related": [
    {
      "to": "/tabs-guide",
      "title": "Dashboard Tabs Guide",
      "description": "A field-by-field walkthrough of the Orders and Customers tabs."
    },
    {
      "to": "/status-guide",
      "title": "Status Badges Guide",
      "description": "What each order status means."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "See which plans include shipping controls and exports."
    }
  ],
  "faqs": [
    {
      "q": "Where do I see all my Instagram orders?",
      "a": "In the Orders tab of your Checkout DM dashboard. Every order placed through the bot appears there with its order number, items, total and status."
    },
    {
      "q": "How do I confirm a UPI or bank transfer payment?",
      "a": "The customer sends a payment screenshot in the DM and it appears in the Orders tab. Check it against your bank account, then press Confirm or Reject."
    },
    {
      "q": "What does Mark as Shipped do?",
      "a": "It records the ship date and, for physical orders, starts the customer\u2019s return window plus a short transit buffer. It is available on Starter, Growth and Pro."
    },
    {
      "q": "Can I log a return myself?",
      "a": "Yes. On a paid order with no return on file, use Log a return. It is not limited by the return window, which only applies to customer requests through the bot."
    },
    {
      "q": "Can I export my orders?",
      "a": "On Growth and Pro you can export customers, orders and returns as a CSV file."
    }
  ]
};

export default post;
